/* eslint-disable import/no-default-export */
import React from 'react';

import { Box, Flex } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

import { OfferCard } from 'sellDirect/components/offer/OfferCard';
import { RefreshErrorModal } from 'sellDirect/components/offer/RefreshErrorModal';
import { getHubHowItWorksSellRoute } from 'shared/helpers/routes';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { PreClosingOffer, useOfferContext } from 'sellDirect/OfferContext';
import { useOptimizelyConversionEvent } from 'platform/useOptimizelyConversionEvent';
import { SellerCta } from 'sellDirect/components/offer/SellerCta';
import { WhatWentIntoYourOffer } from 'sellDirect/components/WhatWentIntoYourOffer';
import { AgentCommissionBox } from 'sellDirect/components/AgentCommissionBox';
import { useMarkOfferAsViewed } from 'platform/graphql/useMarkOfferAsViewed';
import { SellDirectLayout } from 'sellDirect/components/offer/SellDirectLayout';
import { PropertyCardsSellTab } from 'buying/components/offerDash/PropertyCardsSellTab';
import { useTriggerImpressionOnExperiment } from 'shared/graphql/experimentation/useTriggerOptimizelyImpression';
import { EXPERIMENTS } from 'shared/declarations/OptimizelyExperiments';
import { useHotjarTrigger } from 'platform/useHotjarTrigger';
import {
  DisappointmentMessagingPrompt,
  OfferStage,
} from 'sellDirect/components/offer/DisappointmentMessagingPrompt';
import { useAgentReferralPayments } from 'shared/graphql/useAgentReferralPayments';

import { useExperiments } from 'sellDirect/helpers/ExperimentProvider';
import { PriceIncreasePrompt } from 'sellDirect/components/offer/homeConditions/PriceIncreasePrompt';
import { NeutralPriceModal } from 'sellDirect/components/offer/homeConditions/NeutralPriceModal';
import { EntryPointCarousel } from 'sellDirect/components/price-adjustment/EntryPoint';
import { ExpiredLwcCard } from 'sellDirect/routes/lwc/components/shared/ExpiredLwcCard';
import { useParticipantsModal } from 'sellDirect/helpers/ParticipantsProvider';
import { InteractiveEventCard } from 'shared/components/InteractiveEventCard';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { RepeatExperienceModal } from 'sellDirect/routes/lwc/components/shared/repeat-experience/RepeatExperienceModal';
import { SellDirectEvents } from 'sellDirect/components/SellDirectEvents';
import { UnenrollmentModal } from 'sellDirect/routes/lwc/components/shared/UnenrollmentModal';

/**
 * Main page for selling options (though currently this only works for Sell Direct).
 *
 * This page should help the user get to the next step of their offer, no matter which state it's currently in.
 *
 * @throws Error if critical data is missing
 */
export const SellingPage: React.FC<{ offer: PreClosingOffer }> = ({ offer }) => {
  const { lead, blockBuyerContent, sellerInputUUID, eligibleForBwODMessaging, isInLwcPilot } =
    useQualificationContext();
  const { openParticipantsModal } = useParticipantsModal();
  const { isMobile } = useDevice();
  const {
    closeRefreshErrorModal,
    isRefreshErrorModalOpen,
    refreshStatus,
    experimentationData: {
      isInListingsSellTabV2Treatment,
      shouldSeeIntroToSellDirectPreliminaryOfferVideos,
      isInReduceDisappointmentMessagingPreUwExperimentTreatment,
      isInReduceDisappointmentMessagingPreUwExperimentControl,
    },
  } = useOfferContext();
  const {
    assertions: { isLWCRepeatExperienceOn },
  } = useExperimentationContext();

  useMarkOfferAsViewed();
  useOptimizelyConversionEvent('hub_offer_view_conversion');

  const {
    eligibleForAgentReferralPayment,
    fetchingAgentReferralPayments,
    agentCommissionCents,
    agentCommissionPercentOfHeadline,
  } = useAgentReferralPayments(sellerInputUUID, offer.shouldSeeAgentExperience);
  const { priceAdjustment } = useExperiments();

  useTriggerImpressionOnExperiment(
    EXPERIMENTS.PRICE_ADJUSTMENT,
    !(priceAdjustment.validForTreatment || priceAdjustment.validForControl),
  );

  useTriggerImpressionOnExperiment(EXPERIMENTS.LISTINGS_SELL_TAB_V2, !eligibleForBwODMessaging);
  /** Prelim offer video experiment should be available for all direct sellers */
  useTriggerImpressionOnExperiment(
    EXPERIMENTS.INTRO_TO_SELL_DIRECT_PRELIMINARY_OFFER_VIDEOS,
    offer.shouldSeeAgentExperience,
  );

  /*
   * Trigger impression for the reduce-disapointment-messaging-pre-uw experiment
   * if the customer is in control/experiment. Some customers (ie ATX market customers)
   * will not be included in the experiment.
   */
  const inReduceDisappointmentExperiment =
    isInReduceDisappointmentMessagingPreUwExperimentTreatment ||
    isInReduceDisappointmentMessagingPreUwExperimentControl;
  useTriggerImpressionOnExperiment(
    EXPERIMENTS.REDUCE_DISAPPOINTMENT_MESSAGING_PRE_UW,
    !inReduceDisappointmentExperiment,
  );

  useHotjarTrigger('prelim-offer-video', shouldSeeIntroToSellDirectPreliminaryOfferVideos);

  if (!lead) {
    throw new Error('selling-page-missing-lead');
  }
  if (!lead.address) {
    throw new Error('selling-page-missing-lead-address');
  }
  if (!offer) {
    throw new Error('selling-page-missing-offer');
  }

  return (
    <>
      <SellDirectLayout
        isLoading={false}
        address={lead.address}
        stickyBottomContent={isMobile && <SellerCta offer={offer} />}
      >
        <Flex flexDir="column" gridGap={7} width="100%" data-testid="offer-card">
          {/* Display any sell direct events at the top of the page */}
          {offer.isExpired ? null : <SellDirectEvents />}

          {/* Price Adjustment V2 – Increased result */}
          {priceAdjustment.showIncreaseResult && <PriceIncreasePrompt />}

          {isInReduceDisappointmentMessagingPreUwExperimentTreatment &&
            !priceAdjustment.validForTreatment && (
              <DisappointmentMessagingPrompt offerStage={OfferStage.PRE_UW} />
            )}

          <OfferCard offer={offer} />

          {/* Entry point for Price Adjustment V2 */}
          {priceAdjustment.validForTreatment && (
            <EntryPointCarousel
              showMarketConditionCard={isInReduceDisappointmentMessagingPreUwExperimentTreatment}
              showPriceAdjustmentCard={priceAdjustment.showEntryPoint}
              mlsImageUrl={priceAdjustment?.mlsPhotos?.[0]?.imageUrl}
            />
          )}
          {!isMobile && <SellerCta offer={offer} />}
          {isInLwcPilot && offer.isExpired && <ExpiredLwcCard />}
        </Flex>
        <WhatWentIntoYourOffer mt={[8, null, 9]} mb={8} />
        <Flex width="100%">
          <Flex gridGap={5} flexGrow={1} flexDirection={isMobile ? 'column' : 'row'}>
            <InteractiveEventCard
              analyticsName="how-it-works-card"
              chipOrIconProps={{
                name: 'home-cost',
                color: 'brand50',
              }}
              backgroundColor="transparent"
              title={
                blockBuyerContent
                  ? 'How selling works'
                  : 'What it’s like to sell, buy, and close with ease'
              }
              url={getHubHowItWorksSellRoute()}
            />
            <InteractiveEventCard
              analyticsName="invite-participants-card"
              chipOrIconProps={{
                name: 'invite',
                color: 'brand50',
              }}
              backgroundColor="transparent"
              title={'Invite others to join your dashboard'}
              onClick={openParticipantsModal}
            />
          </Flex>
          {isInListingsSellTabV2Treatment && eligibleForBwODMessaging && <PropertyCardsSellTab />}
        </Flex>
        {offer.shouldSeeAgentExperience && agentCommissionPercentOfHeadline != 0 && (
          <Box width="100%" mt={[7, null, 9]}>
            <AgentCommissionBox
              eligibleForAgentReferralPayment={eligibleForAgentReferralPayment}
              agentCommissionCents={agentCommissionCents}
              fetchingAgentReferralPayments={fetchingAgentReferralPayments}
              agentCommissionPercentOfHeadline={agentCommissionPercentOfHeadline}
            />
          </Box>
        )}
      </SellDirectLayout>
      <RefreshErrorModal
        closeModal={closeRefreshErrorModal}
        isOpen={isRefreshErrorModalOpen}
        refreshStatus={refreshStatus}
      />
      {isLWCRepeatExperienceOn && (
        <RepeatExperienceModal
          title="A new way to sell"
          description="See your estimated listing range, info about local agents, and more."
          source="sell-tab"
        />
      )}

      {priceAdjustment.showNeutralResult && <NeutralPriceModal />}
      {/* LWC - Modal to show upon a customer being unenrolled */}
      <UnenrollmentModal />
    </>
  );
};
